import { Dispatch, MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Box,
  Button,
  Card,
  CardHeader,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Flex,
  SearchInput,
  Text,
  Tooltip,
} from '@optiaxiom/react';
import { IconPlus, IconTrashCan } from '@optimizely/axiom-icons';

import { Sidebar } from '@frontend/components/sidebar/Sidebar';
import { ToolRowDetailsSidebar } from '@frontend/components/tools-table/ToolRowDetailsSidebar';
import type { SpecializedAgentAction } from '@frontend/features/agents/hooks';
import { useGetTools } from '@frontend/features/tools-management/hooks/useTools';
import * as agentStyles from '@frontend/styles/form.css';
import { SIDEBAR_WIDTH } from '@frontend/styles/layout.css';
import { Tool } from '@frontend/types/tools';

import { AddToolTable } from './AddToolTable';
import { styles } from './ToolsInput.css';

type ToolsInputProps = {
  agentToolNames: string[];
  dispatch: Dispatch<SpecializedAgentAction>;
  opalInstanceId: string;
  disabled?: boolean;
  selectedTool: Tool | null;
  onToolSelect: (tool: Tool | null) => void;
  toolDialogOpen: boolean;
  onToolDialogOpenChange: (open: boolean) => void;
};
export function ToolsInput({
  agentToolNames,
  dispatch,
  opalInstanceId,
  disabled = false,
  selectedTool,
  onToolSelect,
  toolDialogOpen: controlledDialogOpen,
  onToolDialogOpenChange,
}: ToolsInputProps) {
  const toolText =
    'Specify tools for your agent. Once added, refer to them in your prompt template with `tool_name`';

  const [internalDialogOpen, setInternalDialogOpen] = useState(false);
  const [selectedToolIds, setSelectedToolIds] = useState<string[]>([]);
  const [toolSearchValue, setToolSearchValue] = useState('');
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [toolToRemove, setToolToRemove] = useState<string | null>(null);
  const [highlightedTool, setHighlightedTool] = useState<Tool | null>(null);
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_WIDTH);
  const postDeleteFocusRef = useRef<HTMLElement | null>(null);
  const toolCardRefs = useRef<Map<string, HTMLElement>>(new Map());
  const toolsFieldRef = useRef<HTMLDivElement>(null);

  // Use controlled or uncontrolled state
  const toolDialogOpen = controlledDialogOpen ?? internalDialogOpen;
  const setToolDialogOpen = onToolDialogOpenChange ?? setInternalDialogOpen;

  const { toolsResponse, isPending: toolsLoading } = useGetTools(opalInstanceId, true);

  const availableTools = useMemo(() => {
    return toolsResponse?.functions || [];
  }, [toolsResponse?.functions]);

  const toolsByName = useMemo(() => {
    return new Map(availableTools.map((tool: Tool) => [tool.name, tool]));
  }, [availableTools]);

  // Filter tools based on search value and sort alphabetically by name
  const filteredTools = useMemo(() => {
    return (
      availableTools
        ?.filter(
          (tool: Tool) =>
            tool.name.toLowerCase().includes(toolSearchValue.toLowerCase()) ||
            tool.description.toLowerCase().includes(toolSearchValue.toLowerCase())
        )
        .sort((a: Tool, b: Tool) => a.name.localeCompare(b.name)) || []
    );
  }, [availableTools, toolSearchValue]);

  const handleOpenToolDialog = () => {
    setSelectedToolIds([]);
    setToolSearchValue('');
    setToolDialogOpen(true);
  };

  const handleCloseToolDialog = () => {
    setToolDialogOpen(false);
    setSelectedToolIds([]);
    setToolSearchValue('');
    setHighlightedTool(null);
  };

  const handleSelectTool = useCallback((toolId: string) => {
    setSelectedToolIds(ids =>
      ids.includes(toolId) ? ids.filter(id => id !== toolId) : [...ids, toolId]
    );

    requestAnimationFrame(() => {
      const dialog = document.querySelector('[role="dialog"]') as HTMLElement;
      if (dialog && dialog.scrollTop !== 0) {
        dialog.scrollTop = 0;
      }
    });
  }, []);

  const handleSelectRegistry = useCallback(
    (toolIds: string[]) => {
      const allSelected = toolIds.every(id => selectedToolIds.includes(id));
      if (allSelected) {
        setSelectedToolIds(ids => ids.filter(id => !toolIds.includes(id)));
      } else {
        setSelectedToolIds(ids => [...new Set([...ids, ...toolIds])]);
      }
    },
    [selectedToolIds]
  );

  const handleSelectAll = useCallback(
    (toolIds: string[]) => {
      const allSelected = toolIds.every(id => selectedToolIds.includes(id));
      if (allSelected) {
        setSelectedToolIds([]);
      } else {
        setSelectedToolIds(toolIds);
      }
    },
    [selectedToolIds]
  );

  const handleAddSelectedTools = () => {
    // Find the selected tools by function_id
    const newToolNames = availableTools
      .filter((tool: Tool) => selectedToolIds.includes(tool.function_id))
      .map((tool: Tool) => tool.name)
      .filter((name: string) => !agentToolNames.includes(name)); // Prevent duplicates

    const updatedTools = [...agentToolNames, ...newToolNames];

    dispatch({ type: 'UPDATE_NESTED', field: 'enabled_tools', value: updatedTools });
    setToolDialogOpen(false);
    setSelectedToolIds([]);
    setHighlightedTool(null);
  };

  const handleOpenRemoveDialog = (e: MouseEvent<HTMLButtonElement>, toolName: string) => {
    e.stopPropagation();
    const toolIndex = agentToolNames.indexOf(toolName);
    if (toolIndex > 0) {
      // Focus on the previous tool card
      const prevToolName = agentToolNames[toolIndex - 1];
      postDeleteFocusRef.current = toolCardRefs.current.get(prevToolName) ?? null;
    } else {
      // First tool - focus on the tools field
      postDeleteFocusRef.current = toolsFieldRef.current;
    }
    setToolToRemove(toolName);
    setRemoveDialogOpen(true);
  };

  const handleRemoveTool = () => {
    if (toolToRemove) {
      const updatedTools = agentToolNames.filter(name => name !== toolToRemove);
      dispatch({ type: 'UPDATE_NESTED', field: 'enabled_tools', value: updatedTools });
      if (selectedTool?.name === toolToRemove) {
        onToolSelect(null);
      }
    }
    setRemoveDialogOpen(false);
    setToolToRemove(null);
  };

  return (
    <Flex flexDirection="column" gap="8" pb="8" className={agentStyles.FormInputFieldMaxWidth}>
      <Flex flexDirection="row" gap="16" justifyContent="space-between">
        <Box ref={toolsFieldRef} tabIndex={-1}>
          <Field
            label="Tools"
            info="Extends what Opal can do, such as executing actions or pulling information from other services"
          />
        </Box>
        {agentToolNames.length > 0 && (
          <Button
            appearance="default"
            addonBefore={<IconPlus size="16" />}
            onClick={handleOpenToolDialog}
            disabled={disabled}
          >
            Add Tool
          </Button>
        )}
      </Flex>
      {agentToolNames.length > 0 ? (
        <Flex flexDirection="column" gap="8">
          {agentToolNames.map(toolName => {
            const tool = toolsByName.get(toolName);
            return (
              <Card
                key={toolName}
                bg={disabled ? 'bg.default.hovered' : 'bg.default'}
                style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                p="16"
                tabIndex={-1}
                ref={el => {
                  if (el) {
                    toolCardRefs.current.set(toolName, el);
                  } else {
                    toolCardRefs.current.delete(toolName);
                  }
                }}
              >
                <CardHeader
                  description={
                    tool?.description ? (
                      <Tooltip auto content={tool.description}>
                        <Text lineClamp="2">{tool.description}</Text>
                      </Tooltip>
                    ) : undefined
                  }
                  addonAfter={
                    <Tooltip content="Remove">
                      <Button
                        appearance="subtle"
                        size="sm"
                        icon={<IconTrashCan size={16} />}
                        onClick={e => handleOpenRemoveDialog(e, toolName)}
                        aria-label="Remove tool"
                        disabled={disabled}
                      />
                    </Tooltip>
                  }
                  onClick={() => (disabled ? undefined : onToolSelect(tool ?? null))}
                >
                  <Tooltip auto content={tool?.description}>
                    <Text>{toolName}</Text>
                  </Tooltip>
                </CardHeader>
              </Card>
            );
          })}
        </Flex>
      ) : (
        <Flex
          className={styles.emptySection}
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          bg="bg.secondary"
          p="20"
        >
          <Text color="fg.secondary">{toolText}</Text>
          <Button
            appearance="subtle"
            addonBefore={<IconPlus size="16" />}
            onClick={handleOpenToolDialog}
            disabled={disabled}
          >
            Add Tool
          </Button>
        </Flex>
      )}

      {/* Tool Selection Dialog */}
      <Dialog
        open={toolDialogOpen}
        onOpenChange={open => {
          setToolDialogOpen(open);
          if (!open) {
            setHighlightedTool(null);
          }
        }}
      >
        <DialogContent size="lg" overflow="hidden">
          <Box className={styles.toolsDialogContainer}>
            <Box className={styles.dialogMainArea}>
              <Flex gap="0" style={highlightedTool ? { marginRight: sidebarWidth } : undefined}>
                <DialogHeader bg="bg.page" className={styles.dialogHeaderNoClose}>
                  Add Tools
                </DialogHeader>
                <DialogBody bg="bg.page" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {toolsLoading ? (
                    <Text>Loading tools...</Text>
                  ) : (
                    <Flex flexDirection="column" gap="16">
                      <SearchInput
                        placeholder="Enter name or description"
                        value={toolSearchValue}
                        onChange={e => setToolSearchValue(e.target.value)}
                        w="full"
                        h="40"
                      />
                      {filteredTools && filteredTools.length > 0 ? (
                        <AddToolTable
                          tools={filteredTools}
                          selectedToolIds={selectedToolIds}
                          disabledToolNames={agentToolNames}
                          onSelectTool={handleSelectTool}
                          onSelectRegistry={handleSelectRegistry}
                          onSelectAll={handleSelectAll}
                          onToolHighlight={setHighlightedTool}
                        />
                      ) : (
                        <Text>
                          {toolSearchValue
                            ? `No tools found matching "${toolSearchValue}".`
                            : 'No tools available.'}
                        </Text>
                      )}
                    </Flex>
                  )}
                </DialogBody>
              </Flex>
              <Sidebar
                open={highlightedTool !== null}
                dock="right"
                width={sidebarWidth}
                initialWidth={SIDEBAR_WIDTH}
                maxWidth={SIDEBAR_WIDTH * 2}
                top={0}
                onResize={setSidebarWidth}
              >
                {highlightedTool && (
                  <ToolRowDetailsSidebar
                    selectedTool={highlightedTool}
                    onClose={() => setHighlightedTool(null)}
                    hideFooterCloseButton
                  />
                )}
              </Sidebar>
            </Box>
            <DialogFooter borderT="0">
              <Button appearance="default" onClick={handleCloseToolDialog}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleAddSelectedTools}
                disabled={selectedToolIds.length === 0}
              >
                Add Selected
              </Button>
            </DialogFooter>
          </Box>
        </DialogContent>
      </Dialog>

      {/* AlertDialog for remove confirmation */}
      <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <AlertDialogContent
          onCloseAutoFocus={e => {
            e.preventDefault();
            postDeleteFocusRef.current?.focus();
          }}
        >
          <AlertDialogHeader>Remove Tool?</AlertDialogHeader>
          <AlertDialogBody>
            Are you sure you want to remove the tool &quot;{toolToRemove}&quot;?
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRemoveDialogOpen(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction appearance="danger" onClick={handleRemoveTool}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Flex>
  );
}
